import { useState, useEffect } from 'react';
import axios from 'axios';
import { Loader2, Info, ChevronDown } from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../components/ui/tooltip';
import OptimizedImage from '../components/OptimizedImage';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const allergyIcons = {
  contains_gluten: { label: 'G', fullLabel: 'Gluten', color: 'bg-amber-100 text-amber-700' },
  contains_dairy: { label: 'D', fullLabel: 'Dairy', color: 'bg-blue-100 text-blue-700' },
  contains_nuts: { label: 'N', fullLabel: 'Nuts', color: 'bg-orange-100 text-orange-700' },
  contains_eggs: { label: 'E', fullLabel: 'Eggs', color: 'bg-yellow-100 text-yellow-700' },
  contains_shellfish: { label: 'SF', fullLabel: 'Shellfish', color: 'bg-red-100 text-red-700' },
  contains_soy: { label: 'SY', fullLabel: 'Soy', color: 'bg-green-100 text-green-700' },
  vegetarian: { label: 'V', fullLabel: 'Vegetarian', color: 'bg-emerald-100 text-emerald-700' },
  vegan: { label: 'VE', fullLabel: 'Vegan', color: 'bg-lime-100 text-lime-700' },
  
};

const categoryOrder = [
  'Cold Entrees', 'Hot Entrees', 'Salad', 'Main Courses', 
  'Grills', 'Reva Steaks', 'Side', 'Breads', 'Rice', 'Desserts(P)'
];

const QRMenuPage = () => {
  const [menuItems, setMenuItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState('All');
  const [showAllergyInfo, setShowAllergyInfo] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    fetchMenu();
  }, []);

  const fetchMenu = async () => {
    try {
      const response = await axios.get(`${API}/menu`);
      setMenuItems(response.data);
    } catch (error) {
      console.error('Error fetching menu:', error);
    } finally {
      setLoading(false);
    }
  };

  const getItemImage = (item) => {
    if (item.image_url && !item.image_url.includes('unsplash.com')) {
      return item.image_url;
    }
    return null;
  };

  const getAllergies = (allergies) => {
    if (!allergies) return [];
    return Object.entries(allergies)
      .filter(([key, value]) => value && allergyIcons[key])
      .map(([key]) => ({ key, ...allergyIcons[key] }));
  };

  // Sort categories by predefined order
  const rawCategories = ['All', ...new Set(menuItems.map(item => item.category))];
  const categories = rawCategories.sort((a, b) => {
    if (a === 'All') return -1;
    if (b === 'All') return 1;
    const indexA = categoryOrder.indexOf(a);
    const indexB = categoryOrder.indexOf(b);
    if (indexA === -1) return 1;
    if (indexB === -1) return -1;
    return indexA - indexB;
  });
  
  const filteredItems = activeCategory === 'All' 
    ? menuItems 
    : menuItems.filter(item => item.category === activeCategory);

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-malva" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-gray-50" data-testid="qr-menu-page">
      {/* Sticky Header */}
      <header className="bg-white shadow-sm sticky top-0 z-20">
        <div className="px-4 py-4 border-b border-gray-100">
          <div className="text-center">
            <h1 className="font-serif text-2xl text-gray-800">Reva Dining</h1>
            <p className="text-malva text-sm mt-0.5">Our Menu</p>
          </div>
        </div>
      </header>

      {/* Main Content - Side by Side on larger screens */}
      <div className="flex flex-col md:flex-row">
        {/* Left Sidebar - Categories & Allergens */}
        <aside className="w-full md:w-56 lg:w-64 flex-shrink-0 bg-white md:bg-gray-50 md:border-r border-gray-200">
          {/* Mobile Toggle */}
          <button 
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="md:hidden w-full flex items-center justify-between p-4 border-b border-gray-200"
          >
            <span className="font-medium text-gray-700 text-sm">Categories & Allergens</span>
            <ChevronDown className={`w-5 h-5 text-gray-500 transition-transform ${sidebarOpen ? 'rotate-180' : ''}`} />
          </button>

          <div className={`${sidebarOpen ? 'block' : 'hidden'} md:block md:sticky md:top-[73px] p-4 space-y-4`}>
            {/* Categories */}
            <div>
              <h3 className="font-medium text-gray-800 text-sm mb-3">Categories</h3>
              <div className="space-y-1">
                {categories.map((category) => {
                  const count = category === 'All' 
                    ? menuItems.length 
                    : menuItems.filter(i => i.category === category).length;
                  return (
                    <button
                      key={category}
                      onClick={() => {
                        setActiveCategory(category);
                        setSidebarOpen(false);
                      }}
                      className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm transition-all ${
                        activeCategory === category
                          ? 'bg-malva-dark text-white'
                          : 'text-gray-600 hover:bg-gray-100'
                      }`}
                      data-testid={`qr-category-${category.toLowerCase().replace(/[^a-z0-9]/g, '-')}`}
                    >
                      <span>{category}</span>
                      <span className={`text-xs px-1.5 py-0.5 rounded-full ${
                        activeCategory === category ? 'bg-white/20' : 'bg-gray-200 text-gray-500'
                      }`}>
                        {count}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Allergen Information */}
            <div className="border-t border-gray-200 pt-4">
              <button
                onClick={() => setShowAllergyInfo(!showAllergyInfo)}
                className="w-full flex items-center justify-between py-2 text-gray-800"
                data-testid="qr-allergy-toggle"
              >
                <div className="flex items-center gap-2">
                  <Info className="w-4 h-4 text-malva-dark" />
                  <span className="font-medium text-sm">Allergen Guide</span>
                </div>
                <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${showAllergyInfo ? 'rotate-180' : ''}`} />
              </button>
              
              {showAllergyInfo && (
                <div className="mt-2 pt-2 border-t border-gray-100">
                  <div className="grid grid-cols-2 gap-1">
                    {Object.entries(allergyIcons).map(([key, { label, fullLabel, color }]) => (
                      <div key={key} className="flex items-center gap-1.5 py-1">
                        <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[9px] font-bold ${color}`}>
                          {label}
                        </span>
                        <span className="text-[11px] text-gray-600">{fullLabel}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </aside>

        {/* Right Content - Menu Items */}
        <main className="flex-1 p-4 md:p-6">
          <TooltipProvider>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredItems.map((item, index) => {
                const itemImage = getItemImage(item);
                const allergies = getAllergies(item.allergies);
                
                return (
                  <div 
                    key={item.id} 
                    className="bg-white rounded-xl shadow-sm overflow-hidden border border-gray-100"
                    data-testid={`qr-menu-item-${item.id}`}
                  >
                    {/* Item Image */}
                    <div className="aspect-[4/3] overflow-hidden bg-gradient-to-br from-malva/5 to-gray-100">
                      <OptimizedImage
                        src={itemImage}
                        alt={item.name}
                        className="w-full h-full"
                        width={400}
                        quality={75}
                        priority={index < 6}
                        showPlaceholderOnError={true}
                      />
                    </div>
                    
                    {/* Item Details */}
                    <div className="p-4">
                      <p className="text-malva-dark text-[10px] font-medium tracking-wider uppercase mb-1">{item.category}</p>
                      <h3 className="font-serif text-base text-gray-800 mb-2 leading-snug">{item.name}</h3>
                      <p className="text-gray-500 text-xs leading-relaxed line-clamp-2">
                        {item.description}
                      </p>

                      {/* Allergy Badges */}
                      {allergies.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-3">
                          {allergies.slice(0, 4).map(({ key, label, color }) => (
                            <Tooltip key={key}>
                              <TooltipTrigger asChild>
                                <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold cursor-help ${color}`}>
                                  {label}
                                </span>
                              </TooltipTrigger>
                              <TooltipContent>
                                <p className="text-xs">{allergyIcons[key]?.fullLabel}</p>
                              </TooltipContent>
                            </Tooltip>
                          ))}
                          {allergies.length > 4 && (
                            <span className="w-5 h-5 rounded-full bg-gray-100 text-gray-500 flex items-center justify-center text-[10px]">
                              +{allergies.length - 4}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </TooltipProvider>

          {/* Footer */}
          <footer className="text-center py-8 mt-8 border-t border-gray-100">
            <p className="text-gray-600 font-serif text-lg">Reva Dining</p>
            <p className="text-gray-400 text-xs mt-2">45-47 York St, Twickenham, TW1 3LP</p>
            <p className="text-gray-400 text-xs">+44 20 8892 9841</p>
          </footer>
        </main>
      </div>
    </div>
  );
};

export default QRMenuPage;
